import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Banner, LoadingButton, RHFTextField } from "@components";
import { useLogin } from "@api/v1/hooks/useLogin";
import { preventNonNumericInput } from "@helpers";
import { Link, Typography } from "@mui/material";
import { TwoFAFormData } from "../types";

const FormStyled = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(6)};
`;

const LoadingButtonStyled = styled(LoadingButton)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

interface Props {
  twoFAFormData: TwoFAFormData;
  onLogin: (access_token: string) => void;
  goBack: () => void;
}

interface FormFields {
  code: string;
}

export const TwoFAForm = ({ onLogin, twoFAFormData, goBack }: Props) => {
  const { t } = useTranslation();
  const { loginAsync, isFetching } = useLogin();
  const [showError, setShowError] = useState<boolean>(false);

  const { handleSubmit, control, watch } = useForm<FormFields>({
    defaultValues: { code: "" },
  });

  // eslint-disable-next-line react-hooks/incompatible-library
  const code = watch("code");

  const onFormSubmit = async (data: FormFields) => {
    if (data.code) {
      try {
        setShowError(false);
        const result = await loginAsync({
          grant_type: "two_factor_authentication",
          username: twoFAFormData.email,
          password: twoFAFormData.password,
          mfa_challenge_code: Number(data.code),
          mfa_secret: twoFAFormData.secret,
          rememberMe: twoFAFormData.rememberMe,
        });

        if (result.access_token) {
          onLogin(result.access_token!);
        }
      } catch (e) {
        setShowError(true);
      }
    }
  };

  return (
    <FormStyled onSubmit={handleSubmit(onFormSubmit)} data-testid="two-fa-form">
      {showError && (
        <Banner
          type="error"
          text={
            <Trans
              i18nKey="loginPage.twoFACodeError"
              components={{
                linkText: (
                  <Typography
                    component={Link}
                    onClick={goBack}
                    variant="body3"
                    lineHeight="20px"
                  />
                ),
              }}
            />
          }
          displayDefaultIcon
        />
      )}
      <RHFTextField
        label={t("loginPage.form.twoFACodeLabel")}
        name="code"
        type="number"
        fullWidth
        control={control}
        disabled={isFetching}
        testId="two-fa-code-field"
        onBeforeInput={preventNonNumericInput}
      />
      <LoadingButtonStyled
        fullWidth
        type="submit"
        variant="primary"
        data-testid="submit-two-fa"
        isFetching={isFetching}
        disabled={!code}
      >
        {t("loginPage.form.twoFASubmitButton")}
      </LoadingButtonStyled>
    </FormStyled>
  );
};
